import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { useConfirm } from '../common/ConfirmDialog';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Divider,
  Chip,
  IconButton,
  Stack,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Group as GroupIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Assignment as AssignmentIcon,
  Apps as AppsIcon,
  BlockOutlined as BlockIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import { getCustomerIdFromSubdomain } from '../../utils/constants';
import RevokeAccessDialog from '../common/RevokeAccessDialog';

const containerVariants = {
  initial: {},
  animate: {
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

const itemVariants = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  },
};

const GroupDetails = ({
  group,
  users,
  groupMembers,
  onClose,
  onFetchMembers,
  onAddUsers,
  onRemoveUsers,
  onDelete,
  onRevokeComplete,
}) => {
  const confirm = useConfirm();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  const hasAccess = (group.assignments && group.assignments.length > 0) ||
    (group.applicationAssignments && group.applicationAssignments.length > 0);

  const handleRevokeAccess = async () => {
    setIsRevoking(true);
    const customerId = getCustomerIdFromSubdomain();
    let successCount = 0;
    let failCount = 0;
    const assignments = group.assignments || [];
    const appAssignments = group.applicationAssignments || [];
    const totalCount = assignments.length + appAssignments.length;

    for (const assignment of assignments) {
      try {
        await apiService.deletePermissionSetAssignment(assignment.id);
        successCount++;
      } catch (err) {
        console.error('Failed to revoke assignment:', err);
        failCount++;
      }
    }

    for (const appAssignment of appAssignments) {
      try {
        await apiService.removeGroupFromApplication(customerId, appAssignment.applicationId, group.name);
        successCount++;
      } catch (err) {
        console.error('Failed to revoke app assignment:', err);
        failCount++;
      }
    }

    if (failCount === 0) {
      toast.success(`Successfully revoked ${successCount} of ${totalCount} assignments`);
    } else {
      toast.warning(`Revoked ${successCount} of ${totalCount} assignments (${failCount} failed)`);
    }

    setIsRevoking(false);
    setShowRevokeDialog(false);
    if (onRevokeComplete) onRevokeComplete();
  };

  const membersFetchedRef = useRef(false);
  const currentGroupRef = useRef(null);

  useEffect(() => {
    if (group && group.name && group.name !== currentGroupRef.current) {
      currentGroupRef.current = group.name;
      membersFetchedRef.current = false;

      if (!isLoadingMembers && !membersFetchedRef.current) {
        setIsLoadingMembers(true);
        membersFetchedRef.current = true;

        onFetchMembers(group.name).finally(() => {
          setIsLoadingMembers(false);
        });
      }
    }
  }, [group?.name, onFetchMembers, isLoadingMembers]);

  useEffect(() => {
    if (users && groupMembers) {
      const memberUsernames = new Set(groupMembers.map((member) => member.username));
      const nonMembers = users.filter((user) => !memberUsernames.has(user.username));
      setAvailableUsers(nonMembers);
      setFilteredUsers(nonMembers);
    }
  }, [groupMembers, users]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredUsers(availableUsers);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = availableUsers.filter(
        (user) =>
          user.username.toLowerCase().includes(query) ||
          (user.email && user.email.toLowerCase().includes(query))
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, availableUsers]);

  const handleUserSelection = (user, isSelected) => {
    if (isSelected) {
      setSelectedUsers((prev) => [...prev, user]);
    } else {
      setSelectedUsers((prev) => prev.filter((u) => u.id !== user.id));
    }
  };

  const handleAddUsers = async () => {
    if (selectedUsers.length > 0) {
      const usernames = selectedUsers.map((user) => user.username);
      const success = await onAddUsers(group.name, usernames);
      if (success) {
        setSelectedUsers([]);
      }
    }
  };

  const handleRemoveUser = async (username) => {
    const confirmed = await confirm({
      title: 'Remove User',
      message: `Remove ${username} from ${group.name} group?`,
      confirmText: 'Remove',
      variant: 'warning',
    });
    if (confirmed) {
      await onRemoveUsers(group.name, [username]);
    }
  };

  const handleDeleteGroup = async () => {
    if (isDeleting) return;

    try {
      setIsDeleting(true);
      const success = await onDelete(group.name);
      if (success) {
        onClose();
      }
    } catch (error) {
      console.error('Error deleting group:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const isUserSelected = (user) => {
    return selectedUsers.some((selectedUser) => selectedUser.id === user.id);
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#FFFFFF',
      borderRadius: '6px',
      fontFamily: 'Metropolis, sans-serif',
      '& fieldset': {
        borderColor: '#E5E5E5',
      },
      '&:hover fieldset': {
        borderColor: '#D64794',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#D64794',
      },
    },
  };

  return (
    <motion.div variants={containerVariants} initial="initial" animate="animate">
      {/* Header */}
      <motion.div variants={itemVariants}>
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid #E5E5E5',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              backgroundColor: '#D64794',
              width: 48,
              height: 48,
            }}
          >
            <GroupIcon sx={{ fontSize: 24 }} />
          </Avatar>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Bitter, Georgia, serif',
                fontWeight: 600,
                fontSize: '1.5rem',
                color: '#253E66',
              }}
            >
              {group.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#999999',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 500,
              }}
            >
              Group Details
            </Typography>
          </Box>
        </Box>
      </Box>
      </motion.div>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        {/* Group Assignments */}
        <motion.div variants={itemVariants}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <AssignmentIcon sx={{ color: '#7B1FA2', fontSize: 20 }} />
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                color: '#253E66',
              }}
            >
              Assignments
            </Typography>
            {group.isLoadingAssignments && (
              <CircularProgress size={16} sx={{ color: '#7B1FA2' }} />
            )}
          </Box>

          <Box sx={{ pl: 0.5 }}>
            {group.isLoadingAssignments ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={16} sx={{ color: '#7B1FA2' }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  Loading assignments...
                </Typography>
              </Box>
            ) : group.assignments && group.assignments.length > 0 ? (
              <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
              <TableContainer sx={{ backgroundColor: '#F9FBFD', borderRadius: '8px', border: '1px solid #E5E5E5' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Permission Set', 'Account'].map((header) => (
                        <TableCell
                          key={header}
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            color: '#253E66',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            borderBottom: '2px solid #E5E5E5',
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.assignments.map((assignment) => (
                      <TableRow key={assignment.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 500,
                            color: '#253E66',
                            fontSize: '0.85rem',
                          }}
                        >
                          {assignment.permissionSetName}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            color: '#253E66',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              fontWeight: 500,
                              color: '#253E66',
                              fontSize: '0.85rem',
                              lineHeight: 1.3,
                            }}
                          >
                            {assignment.accountName || assignment.accountId}
                          </Typography>
                          {assignment.accountName && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: 'Metropolis, sans-serif',
                                color: '#999999',
                                fontSize: '0.7rem',
                              }}
                            >
                              ({assignment.accountId})
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </motion.div>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: '#999999',
                  fontFamily: 'Metropolis, sans-serif',
                  fontStyle: 'italic',
                  fontWeight: 500,
                }}
              >
                No permission set assignments found for this group
              </Typography>
            )}
          </Box>
        </Box>
        </motion.div>

        {/* Application Assignments Section */}
        <motion.div variants={itemVariants}>
        <Box sx={{ mb: 3, mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <AppsIcon sx={{ color: '#8E44AD', fontSize: 20 }} />
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                color: '#253E66',
              }}
            >
              Application Assignments
            </Typography>
            {group.isLoadingAssignments && (
              <CircularProgress size={16} sx={{ color: '#8E44AD' }} />
            )}
          </Box>

          <Box sx={{ pl: 0.5 }}>
            {group.isLoadingAssignments ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={16} sx={{ color: '#8E44AD' }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#999999',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  Loading application assignments...
                </Typography>
              </Box>
            ) : group.applicationAssignments && group.applicationAssignments.length > 0 ? (
              <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
              <TableContainer sx={{ backgroundColor: '#F9FBFD', borderRadius: '8px', border: '1px solid #E5E5E5' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                          color: '#253E66',
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          borderBottom: '2px solid #E5E5E5',
                        }}
                      >
                        Application Name
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.applicationAssignments.map((appAssignment) => (
                      <TableRow key={appAssignment.applicationId} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 500,
                            color: '#253E66',
                            fontSize: '0.85rem',
                          }}
                        >
                          {appAssignment.applicationName || appAssignment.clientId}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </motion.div>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: '#999999',
                  fontFamily: 'Metropolis, sans-serif',
                  fontStyle: 'italic',
                  fontWeight: 500,
                }}
              >
                No application assignments found for this group
              </Typography>
            )}
          </Box>
        </Box>
        </motion.div>

        <Divider sx={{ my: 3, borderColor: '#E5E5E5' }} />

        {/* Group Members */}
        <motion.div variants={itemVariants}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <PersonIcon sx={{ color: '#D64794', fontSize: 20 }} />
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                color: '#253E66',
              }}
            >
              Group Members ({groupMembers ? groupMembers.length : 0})
            </Typography>
            {isLoadingMembers && (
              <CircularProgress size={16} sx={{ color: '#D64794' }} />
            )}
          </Box>

          {isLoadingMembers ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={16} sx={{ color: '#D64794' }} />
              <Typography
                variant="body2"
                sx={{
                  color: '#999999',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 500,
                }}
              >
                Loading members...
              </Typography>
            </Box>
          ) : groupMembers && groupMembers.length > 0 ? (
            <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            <Box
              sx={{
                backgroundColor: '#F9FBFD',
                borderRadius: '6px',
                border: '1px solid #E5E5E5',
                maxHeight: '200px',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#F9FBFD',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#D9DBDC',
                  borderRadius: '3px',
                },
              }}
            >
              <List sx={{ py: 0 }}>
                {groupMembers.map((member, index) => (
                  <ListItem
                    key={member.username}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveUser(member.username)}
                        sx={{
                          color: '#E74C3C',
                          '&:hover': {
                            backgroundColor: 'rgba(231, 76, 60, 0.1)',
                          },
                        }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    }
                    sx={{
                      borderBottom:
                        index < groupMembers.length - 1
                          ? '1px solid #E5E5E5'
                          : 'none',
                      py: 1.5,
                      px: 2,
                    }}
                  >
                    <ListItemText
                      primary={member.username}
                      secondary={member.email}
                      primaryTypographyProps={{
                        sx: {
                          color: '#253E66',
                          fontWeight: 500,
                          fontFamily: 'Metropolis, sans-serif',
                        },
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          color: '#999999',
                          fontFamily: 'Metropolis, sans-serif',
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            </motion.div>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: '#999999',
                fontStyle: 'italic',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 500,
              }}
            >
              This group has no members
            </Typography>
          )}
        </Box>
        </motion.div>

        {/* Add Members */}
        {availableUsers && availableUsers.length > 0 && (
          <motion.div variants={itemVariants}>
          <>
            <Divider sx={{ my: 3, borderColor: '#E5E5E5' }} />
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <PersonAddIcon sx={{ color: '#27AE60', fontSize: 20 }} />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    color: '#253E66',
                  }}
                >
                  Add Members
                </Typography>
              </Box>

              {/* Search input */}
              <TextField
                fullWidth
                placeholder="Search users..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ ...inputStyles, mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999999' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Box
                sx={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  backgroundColor: '#F9FBFD',
                  borderRadius: '6px',
                  border: '1px solid #E5E5E5',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#F9FBFD',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#D9DBDC',
                    borderRadius: '3px',
                  },
                }}
              >
                <List sx={{ p: 0 }} dense>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user, index) => (
                      <ListItem
                        key={user.id || user.username}
                        sx={{
                          py: 1,
                          px: 2,
                          borderBottom:
                            index < filteredUsers.length - 1
                              ? '1px solid #E5E5E5'
                              : 'none',
                          '&:hover': {
                            backgroundColor: '#FFFFFF',
                          },
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isUserSelected(user)}
                              onChange={(e) =>
                                handleUserSelection(user, e.target.checked)
                              }
                              sx={{
                                color: '#BDBDBD',
                                '&.Mui-checked': {
                                  color: '#27AE60',
                                },
                              }}
                            />
                          }
                          label={
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                            >
                              <Avatar
                                sx={{
                                  width: 28,
                                  height: 28,
                                  backgroundColor: isUserSelected(user)
                                    ? '#27AE60'
                                    : '#E5E5E5',
                                  color: isUserSelected(user) ? '#FFFFFF' : '#666666',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  fontFamily: 'Metropolis, sans-serif',
                                }}
                              >
                                {user.username
                                  ? user.username.charAt(0).toUpperCase()
                                  : '?'}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#253E66',
                                    fontWeight: 500,
                                    fontFamily: 'Metropolis, sans-serif',
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {user.username}
                                </Typography>
                                {user.email && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#999999',
                                      fontFamily: 'Metropolis, sans-serif',
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {user.email}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          }
                          sx={{
                            mx: 0,
                            width: '100%',
                            '& .MuiFormControlLabel-label': {
                              width: '100%',
                            },
                          }}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#999999',
                          fontStyle: 'italic',
                          textAlign: 'center',
                          width: '100%',
                          py: 2,
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 500,
                        }}
                      >
                        No users match your search
                      </Typography>
                    </ListItem>
                  )}
                </List>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}
                >
                  {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''}{' '}
                  selected
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedUsers([])}
                    disabled={selectedUsers.length === 0}
                    sx={{
                      color: '#666666',
                      borderColor: '#E5E5E5',
                      textTransform: 'none',
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                      borderRadius: '6px',
                      '&:hover': {
                        borderColor: '#BDBDBD',
                        backgroundColor: '#F9FBFD',
                      },
                      '&.Mui-disabled': {
                        color: '#BDBDBD',
                        borderColor: '#E5E5E5',
                      },
                    }}
                  >
                    Clear
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddUsers}
                    disabled={selectedUsers.length === 0}
                    sx={{
                      backgroundColor: '#27AE60',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontFamily: 'Metropolis, sans-serif',
                      borderRadius: '6px',
                      '&:hover': {
                        backgroundColor: '#219A52',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#E5E5E5',
                        color: '#BDBDBD',
                      },
                    }}
                  >
                    Add Selected ({selectedUsers.length})
                  </Button>
                </Box>
              </Box>
            </Box>
          </>
          </motion.div>
        )}

        {/* Action Buttons */}
        <motion.div variants={itemVariants}>
        <Divider sx={{ my: 3, borderColor: '#E5E5E5' }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<BlockIcon />}
            onClick={() => setShowRevokeDialog(true)}
            disabled={!hasAccess}
            sx={{
              color: '#E74C3C',
              borderColor: 'rgba(231, 76, 60, 0.3)',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#E74C3C',
                backgroundColor: 'rgba(231, 76, 60, 0.05)',
              },
              '&.Mui-disabled': {
                color: '#BDBDBD',
                borderColor: '#E5E5E5',
              },
            }}
          >
            Revoke Access
          </Button>

          <Button
            variant="contained"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteGroup}
            disabled={isDeleting}
            sx={{
              backgroundColor: '#E74C3C',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#C0392B',
              },
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Group'}
          </Button>
        </Box>
        </motion.div>

        {/* Revoke Access Dialog */}
        <RevokeAccessDialog
          open={showRevokeDialog}
          onClose={() => setShowRevokeDialog(false)}
          onConfirm={handleRevokeAccess}
          principalType="group"
          principalName={group.name}
          assignments={group.assignments || []}
          applicationAssignments={group.applicationAssignments || []}
          isRevoking={isRevoking}
        />
      </Box>
    </motion.div>
  );
};

export default GroupDetails;
